import React from 'react';
import { AccessibleDocument } from 'signer-app/signer-signature-document/accessible-document';
import { Drawer } from '@dropbox/dig-components/drawer';
import { NextButton } from 'signer-app/signer-experience/action-buttons/next-button';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSignatureRequestContext } from 'signer-app/signature-request/context';
import { unreachable } from 'signer-app/utils/unreachable';
import { Text, Title } from '@dropbox/dig-components/typography';
import { PreviousButton } from 'signer-app/signer-experience/action-buttons/previous-button';
import styles from './signer-drawer.module.css';
import { ChevronRightLine } from '@dropbox/dig-icons/assets';
import { UIIcon } from '@dropbox/dig-icons';
import classNames from 'classnames';
import { useMatchMedia } from 'signer-app/utils/use-match-media';
import { SignerAppParams, useSignerContext } from './signer-experience';
import { Box } from '@dropbox/dig-foundations';
import { LegalConfirm } from './legal-confirm';
import { useBundleProps } from 'signer-app/bundle-props';
import { AttachmentsField } from 'signer-app/signer-signature-document/field-components/attachments-field';
import messages from './signer-toolbar/messages';
import { useAttachmentsInfo } from 'signer-app/utils/use-attachments-info';
import { WhiteLabelThemeProvider } from 'signer-app/white-labeling/theme-provider';

function Review() {
  const { whiteLabelStyles } = useBundleProps();

  return (
    <Box paddingX="Micro XLarge" paddingY="Micro Large">
      <Title size="small" isBold className={styles.reviewToolbarTitle}>
        <FormattedMessage
          id="signer.confirm.title"
          description="text in signer flow, informing signer the process of signing the document is almost done"
          defaultMessage="Almost done."
        />
      </Title>
      <Text color="standard" size="medium">
        <LegalConfirm
          legalVersion={whiteLabelStyles?.legalVersion ?? 'terms1'}
        />
      </Text>
    </Box>
  );
}

function Attachments() {
  return (
    <Box paddingX="Micro Large" paddingTop="Micro Small">
      <AttachmentsField />
    </Box>
  );
}

export function SignerDrawer({
  isOpen,
  step,
}: {
  isOpen: boolean;
  step: SignerAppParams['step'];
}): JSX.Element {
  const { selectedFieldIds } = useSignatureRequestContext();
  const intl = useIntl();
  const { requiredAttachmentsLeft } = useAttachmentsInfo();
  const { requiredFieldsLeft, signerFields } = useSignerContext();

  const field = React.useMemo(
    () => signerFields.find((f) => f.id === selectedFieldIds[0]),
    [selectedFieldIds, signerFields],
  );
  const smallestViewport = useMatchMedia('(max-width: 389px)');

  const doneFields = React.useMemo(
    () =>
      signerFields.filter((field) => {
        switch (field.type) {
          case 'signature':
          case 'initials':
            return field.signature?.guid !== undefined;
          case 'checkbox':
          case 'radiobutton':
            return field.checked === true;
          case 'text':
          case 'date':
          case 'dropdown':
            return field.value !== undefined && field.value !== '';
          case 'hyperlink':
          case 'image':
          case 'rectangle':
            return false;
          default:
            unreachable(field);
        }
        return false;
      }).length,
    [signerFields],
  );

  const statusMessage = React.useMemo(() => {
    if (step === 'preview') {
      return intl.formatMessage(messages.requiredFieldsAndAttachments, {
        requiredFieldsLeft,
        requiredAttachmentsLeft,
      });
    }

    return (
      <FormattedMessage
        id="4cc66419f6a83923fe9a99045b60e227ec7c33e6715ace2ddb1046b159294a1a"
        defaultMessage="{done}/{total} filled out"
        description="Drawer footer message indicating how many fields have been filled out"
        values={{
          done: doneFields,
          total: signerFields.length,
        }}
      />
    );
  }, [
    step,
    intl,
    requiredFieldsLeft,
    requiredAttachmentsLeft,
    doneFields,
    signerFields,
  ]);

  return (
    <Drawer.InlineDrawer
      isOpen={isOpen}
      className={styles.inlineDrawer}
      alignment="bottom"
      height="auto"
      data-testid="signer-drawer"
    >
      <WhiteLabelThemeProvider mode="bright">
        <Drawer.Container className={styles.container}>
          <Drawer.Body
            className={classNames(styles.body, {
              [styles.field]: !!field,
            })}
          >
            {
              // eslint-disable-next-line no-nested-ternary
              step === 'review' ? (
                <Review />
              ) : step === 'attachments' ? (
                <Attachments />
              ) : (
                <AccessibleDocument
                  className={styles.formView}
                  onlySelected={true}
                />
              )
            }
          </Drawer.Body>
          <Drawer.Footer isBorderHidden className={styles.footer}>
            <Drawer.Layout>
              <Drawer.LayoutItem shift="left" width="fill">
                <Text size="small">{statusMessage}</Text>
              </Drawer.LayoutItem>
              <Drawer.LayoutItem shift="right" width="hug">
                <PreviousButton />
              </Drawer.LayoutItem>
              <Drawer.LayoutItem shift="right" width="hug">
                <Box marginLeft="Micro Small">
                  <NextButton size="medium" icon={smallestViewport}>
                    {smallestViewport && <UIIcon src={ChevronRightLine} />}
                  </NextButton>
                </Box>
              </Drawer.LayoutItem>
            </Drawer.Layout>
          </Drawer.Footer>
        </Drawer.Container>
      </WhiteLabelThemeProvider>
    </Drawer.InlineDrawer>
  );
}
